'use client';

import { useEffect, useMemo, useReducer, useRef, useState } from 'react';

import { useQuery } from '@apollo/client/react';
import { css } from '@emotion/react';
import { Tab, Tabs, Box, Skeleton, Typography } from '@mui/material';
import { EventItem, useItems } from '@scorenco/components';
import { useTranslation, RouteLink, Types } from '@scorenco/core';
import { useTheme } from '@mui/material/styles';
import { startOfWeek, endOfWeek, addDays } from 'date-fns';
import { useRouter } from 'next/router';

import { T_KEYS } from '@/translations';

import { TabPanel } from '../../TabPanel';

import {
  GetWeekEventsByTeamsDocument,
  GetWeekEventsByTeamsRankSortDocument,
  GetWeekEventsDocument,
  GetWeekEventsRankSortDocument,
} from './_internal/Widget.query.generated';
import type {
  GetWeekEventsQuery,
  GetWeekEventsByTeamsQuery,
  GetWeekEventsRankSortQuery,
  GetWeekEventsByTeamsRankSortQuery,
} from './_internal/Widget.query.generated';

type WeekEventsQueryData =
  | GetWeekEventsQuery
  | GetWeekEventsByTeamsQuery
  | GetWeekEventsRankSortQuery
  | GetWeekEventsByTeamsRankSortQuery;

const dateToString = (date: Date) =>
  `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;

type EventTeam = {
  club_id?: number;
};

const getNumberFromUnknown = (value: unknown): number | undefined =>
  typeof value === 'number' ? value : undefined;

const getDateInput = (value: unknown): string | number | Date | undefined => {
  if (typeof value === 'string' || typeof value === 'number') {
    return value;
  }

  return value instanceof Date ? value : undefined;
};

const getEventTeams = (value: unknown): EventTeam[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .filter(
      (item): item is Record<string, unknown> =>
        typeof item === 'object' && item !== null
    )
    .map((team) => ({
      club_id: getNumberFromUnknown(team.club_id),
    }));
};

const getDatesOfWeek = (nbWeeks = 0) => {
  const currentDate = addDays(new Date(), nbWeeks * 7);

  return {
    startDate: dateToString(startOfWeek(currentDate, { weekStartsOn: 1 })),
    endDate: dateToString(endOfWeek(currentDate, { weekStartsOn: 1 })),
  };
};

const WeekTabContent = ({
  foundWeek,
  requestCount,
  maxRequests,
  getWeekEventsData,
  clubId,
  weekOffset,
  teams,
  sort,
  eventsAwayHome,
  settings,
}: {
  foundWeek: boolean;
  requestCount: number;
  maxRequests: number;
  getWeekEventsData: WeekEventsQueryData | undefined;
  clubId?: number;
  weekOffset: number;
  teams?: { team_id?: number }[];
  sort?: string;
  eventsAwayHome?: string;
  settings?: { show_name_in_club?: boolean };
}) => {
  if (foundWeek) {
    if (requestCount >= maxRequests && !getWeekEventsData) {
      return (
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography>Aucun match trouvé</Typography>
        </Box>
      );
    }
    return (
      <WeekEvents
        clubId={clubId}
        week={weekOffset}
        teams={teams}
        sort={sort}
        eventsAwayHome={eventsAwayHome}
        settings={settings}
      />
    );
  }

  return (
    <Box sx={{ p: 2, textAlign: 'center' }}>
      <Typography>Aucun match cette semaine</Typography>
    </Box>
  );
};

type WeekEventsProps = {
  clubId?: number;
  week: number;
  teams?: { team_id?: number }[];
  sort?: string;
  eventsAwayHome?: string;
  onWeekHasEvents?: (hasEvents: boolean) => void;
  settings?: {
    show_name_in_club?: boolean;
  };
};

const WeekEvents = ({
  clubId,
  week,
  teams,
  sort,
  eventsAwayHome,
  onWeekHasEvents,
  settings,
}: WeekEventsProps) => {
  const router = useRouter();
  const theme = useTheme();

  const { startDate, endDate } = useMemo(() => getDatesOfWeek(week), [week]);

  const getWeekEvents = useQuery(GetWeekEventsDocument, {
    variables: {
      clubId: clubId!,
      startDate,
      endDate,
      cacheTTL: router.query.noCache ? 0 : undefined,
    },
    skip: !clubId || (teams?.length ?? 0) > 0 || sort === 'rank',
    context: {
      role: 'anonymous',
    },
  });
  const getWeekEventsByTeams = useQuery(GetWeekEventsByTeamsDocument, {
    variables: {
      clubId: clubId!,
      startDate,
      endDate,
      teamIds: teams
        ?.map((team) => team.team_id)
        .filter((id): id is number => id !== undefined),
      cacheTTL: router.query.noCache ? 0 : undefined,
    },
    skip: !clubId || teams?.length === 0 || sort === 'rank',
    context: {
      role: 'anonymous',
    },
  });
  const getWeekEventsRankSort = useQuery(GetWeekEventsRankSortDocument, {
    variables: {
      clubId: clubId!,
      startDate,
      endDate,
      cacheTTL: router.query.noCache ? 0 : undefined,
    },
    skip: !clubId || (teams?.length ?? 0) > 0 || sort !== 'rank',
    context: {
      role: 'anonymous',
    },
  });
  const getWeekEventsByTeamsRankSort = useQuery(
    GetWeekEventsByTeamsRankSortDocument,
    {
      variables: {
        clubId: clubId!,
        startDate,
        endDate,
        teamIds: teams
          ?.map((team) => team.team_id)
          .filter((id): id is number => id !== undefined),
        cacheTTL: router.query.noCache ? 0 : undefined,
      },
      skip: !clubId || teams?.length === 0 || sort !== 'rank',
      context: {
        role: 'anonymous',
      },
    }
  );

  const events = useItems(
    sort === 'rank'
      ? teams?.length === 0
        ? getWeekEventsRankSort.data
            ?.competitions_event_detail_by_club_id_between_dates_order_by_rank
        : getWeekEventsByTeamsRankSort.data
            ?.competitions_event_detail_by_club_id_between_dates_order_by_rank
      : teams?.length === 0
        ? getWeekEvents.data?.competitions_event_detail_by_club_id_between_dates
        : getWeekEventsByTeams.data
            ?.competitions_event_detail_by_club_id_between_dates,
    5
  );

  const eventsFiltered = events?.filter((event) => {
    const teams = getEventTeams(event?.teams);

    return (
      eventsAwayHome === 'all' ||
      (eventsAwayHome === 'home' && teams[0]?.club_id === clubId) ||
      (eventsAwayHome === 'away' && teams[1]?.club_id === clubId)
    );
  });

  // Enrichir les événements avec leur date locale pour le groupement
  const eventsWithLocalDate = eventsFiltered
    ?.filter((event): event is NonNullable<typeof event> => event !== null)
    .flatMap((event) => {
      const eventDate = getDateInput(event.time) ?? getDateInput(event.date);
      if (!eventDate) {
        return [];
      }

      return [
        {
          ...event,
          day: new Date(eventDate).toLocaleDateString('fr-FR', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
          }),
        },
      ];
    });

  useEffect(() => {
    if (onWeekHasEvents) {
      onWeekHasEvents(eventsWithLocalDate?.length > 0);
    }
  }, [eventsWithLocalDate?.length, onWeekHasEvents]);

  if (eventsWithLocalDate?.length === 0 && onWeekHasEvents) {
    return (
      <Typography
        variant="h5"
        className="no-info"
        sx={{
          width: '100%',
          height: '5rem',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-end',
        }}
      >
        Aucune information actuellement
      </Typography>
    );
  }

  return (
    <>
      {eventsWithLocalDate?.map((event, idx) => {
        if (!event?.id) {
          return (
            <Skeleton
              key={idx}
              className="event-skeleton"
              sx={{ width: '100%', height: '3rem' }}
            />
          );
        }

        // Utilise la date locale pour déterminer si on doit afficher le séparateur
        const showDateSeparator =
          idx === 0 || event?.day !== eventsWithLocalDate?.[idx - 1]?.day;

        return (
          <>
            {showDateSeparator && (
              <Box
                className="date-separator"
                css={css`
                  width: 100%;
                  text-align: center;
                  /* vars = var(--snc-palette-*) : suit les surcharges WidgetsGlobalStyles */
                  background-color: ${theme.vars.palette.primary.main};
                  color: ${theme.vars.palette.primary.contrastText};
                  border-top: ${idx > 0 && '1px solid'};
                  border-bottom: 1px solid;
                  border-color: ${theme.palette.divider};
                  padding: 0.3rem 0;
                  font-size: 0.8rem;
                  margin-top: ${idx > 0 && '0.8rem'};
                  &:first-letter {
                    text-transform: uppercase;
                  }
                `}
              >
                {event?.day}
              </Box>
            )}
            <RouteLink
              href={event.url && process.env.NEXT_PUBLIC_WEB_URL + event.url}
            >
              <a
                className="event-anchor"
                target="_blank"
                style={{ textDecoration: 'none', color: 'unset' }}
              >
                <EventItem
                  key={event.id}
                  className="event-item"
                  event={event as Types.Competitions_Event_Detail}
                  clubId={clubId}
                  showShortName={settings?.show_name_in_club}
                />
              </a>
            </RouteLink>
          </>
        );
      })}
    </>
  );
};

type WeekEventsWidgetProps = {
  clubId?: number;
  teams?: { team_id?: number }[];
  sort?: string;
  daySwitch?: string;
  eventsAwayHome?: string;
  settings?: {
    show_name_in_club?: boolean;
  };
};

export const WeekEventsWidget = ({
  teams,
  sort,
  clubId,
  daySwitch,
  eventsAwayHome,
  settings = { show_name_in_club: true },
}: WeekEventsWidgetProps) => {
  const { t } = useTranslation();

  const defaultTab = useMemo(() => {
    const today = new Date().getDay();
    switch (daySwitch) {
      case 'never':
        return 0;
      case 'now':
        return 1;
      case 'monday':
        return 1 <= today || today === 0 ? 1 : 0;
      case 'tuesday':
        return 2 <= today || today === 0 ? 1 : 0;
      case 'wednesday':
        return 3 <= today || today === 0 ? 1 : 0;
      case 'thursday':
        return 4 <= today || today === 0 ? 1 : 0;
      case 'friday':
        return 5 <= today || today === 0 ? 1 : 0;
      case 'saturday':
        return 6 <= today || today === 0 ? 1 : 0;
      case 'sunday':
        return today === 0 ? 1 : 0;
      default:
        return 1;
    }
  }, [daySwitch]);

  const [userTab, setUserTab] = useState<number | null>(null);
  const currentTab = userTab ?? defaultTab;

  type SearchState = {
    weekOffsets: { [key: number]: number };
    foundWeeks: { [key: number]: boolean };
    requestCount: { [key: number]: number };
  };

  type SearchAction =
    | { type: 'reset' }
    | { type: 'found'; tab: number }
    | { type: 'found_and_reset_next'; tab: number }
    | { type: 'shift_week'; tab: number; newWeek: number }
    | { type: 'max_reached'; tab: number }
    | { type: 'increment_request'; tab: number };

  const initialSearchState: SearchState = {
    weekOffsets: { 0: -1, 1: 0, 2: 1 },
    foundWeeks: { 0: false, 1: false, 2: false },
    requestCount: { 0: 0, 1: 0, 2: 0 },
  };

  const searchReducer = (
    state: SearchState,
    action: SearchAction
  ): SearchState => {
    switch (action.type) {
      case 'reset':
        return initialSearchState;
      case 'increment_request':
        return {
          ...state,
          requestCount: {
            ...state.requestCount,
            [action.tab]: state.requestCount[action.tab] + 1,
          },
        };
      case 'found':
        return {
          ...state,
          foundWeeks: { ...state.foundWeeks, [action.tab]: true },
        };
      case 'found_and_reset_next':
        return {
          ...state,
          foundWeeks: {
            ...state.foundWeeks,
            [action.tab]: true,
            2: false,
          },
          weekOffsets: {
            ...state.weekOffsets,
            2: state.weekOffsets[1] + 1,
          },
          requestCount: { ...state.requestCount, 2: 0 },
        };
      case 'shift_week':
        return {
          ...state,
          weekOffsets: {
            ...state.weekOffsets,
            [action.tab]: action.newWeek,
          },
        };
      case 'max_reached':
        return {
          ...state,
          foundWeeks: { ...state.foundWeeks, [action.tab]: true },
        };
      default:
        return state;
    }
  };

  const [searchState, dispatch] = useReducer(searchReducer, initialSearchState);
  const { weekOffsets, foundWeeks, requestCount } = searchState;

  // Reset search state when dependencies change
  const resetKey = JSON.stringify({ clubId, teams, sort, eventsAwayHome });
  const prevResetKeyRef = useRef(resetKey);

  useEffect(() => {
    if (resetKey !== prevResetKeyRef.current) {
      prevResetKeyRef.current = resetKey;
      dispatch({ type: 'reset' });
    }
  }, [resetKey]);

  const MAX_REQUESTS = 10;

  const getWeekEvents = useQuery(
    teams?.length === 0
      ? sort === 'rank'
        ? GetWeekEventsRankSortDocument
        : GetWeekEventsDocument
      : sort === 'rank'
        ? GetWeekEventsByTeamsRankSortDocument
        : GetWeekEventsByTeamsDocument,
    {
      variables: {
        clubId: clubId!,
        ...getDatesOfWeek(weekOffsets[currentTab]),
        ...((teams?.length ?? 0) > 0 && {
          teamIds: teams!
            .map((team) => team.team_id)
            .filter((id): id is number => id !== undefined),
        }),
        cacheTTL: 0,
      },
      skip:
        !clubId ||
        foundWeeks[currentTab] ||
        requestCount[currentTab] >= MAX_REQUESTS,
      context: { role: 'anonymous' },
    }
  );

  useEffect(() => {
    if (getWeekEvents.data) {
      dispatch({ type: 'increment_request', tab: currentTab });

      const data = getWeekEvents.data as Record<string, unknown> | undefined;
      const events = (
        sort === 'rank'
          ? (
              data as
                | GetWeekEventsRankSortQuery
                | GetWeekEventsByTeamsRankSortQuery
                | undefined
            )?.competitions_event_detail_by_club_id_between_dates_order_by_rank
          : (data as GetWeekEventsQuery | GetWeekEventsByTeamsQuery | undefined)
              ?.competitions_event_detail_by_club_id_between_dates
      ) as Array<{ teams?: Array<{ club_id?: number }> }> | undefined;

      const hasEvents = events?.some(
        (event) =>
          eventsAwayHome === 'all' ||
          (eventsAwayHome === 'home' &&
            event?.teams?.[0]?.club_id === clubId) ||
          (eventsAwayHome === 'away' && event?.teams?.[1]?.club_id === clubId)
      );

      if (hasEvents) {
        if (currentTab === 1) {
          dispatch({ type: 'found_and_reset_next', tab: currentTab });
        } else {
          dispatch({ type: 'found', tab: currentTab });
        }
      } else if (requestCount[currentTab] < MAX_REQUESTS) {
        const newWeek =
          currentTab === 0
            ? Math.max(weekOffsets[currentTab] - 1, -12)
            : Math.min(
                weekOffsets[currentTab] + 1,
                currentTab === 1 ? 12 : weekOffsets[1] + 12
              );

        if (newWeek !== weekOffsets[currentTab]) {
          dispatch({ type: 'shift_week', tab: currentTab, newWeek });
          getWeekEvents.refetch({
            ...getDatesOfWeek(newWeek),
          });
        } else {
          dispatch({ type: 'max_reached', tab: currentTab });
        }
      } else {
        dispatch({ type: 'max_reached', tab: currentTab });
      }
    }
  }, [getWeekEvents.data, currentTab, clubId, eventsAwayHome, sort]);

  const handleChangeTab = (_event: React.SyntheticEvent, newValue: number) => {
    setUserTab(newValue);
  };

  return (
    <Box
      className="week-events-widget"
      sx={{ width: '100%', marginBottom: '1.5rem' }}
    >
      <Box
        className="tab-container"
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 0.5 }}
      >
        <Tabs
          value={currentTab}
          onChange={handleChangeTab}
          centered
          variant="fullWidth"
          className="tabs"
        >
          <Tab label={t(T_KEYS.PREVIOUS)} className="tab-previous" />
          <Tab label={t(T_KEYS.CURRENT)} className="tab-current" />
          <Tab label={t(T_KEYS.NEXT)} className="tab-next" />
        </Tabs>
      </Box>

      <TabPanel value={currentTab} index={0} className="tab-panel-previous">
        <WeekTabContent
          foundWeek={foundWeeks[0]}
          requestCount={requestCount[0]}
          maxRequests={MAX_REQUESTS}
          getWeekEventsData={
            getWeekEvents.data as WeekEventsQueryData | undefined
          }
          clubId={clubId}
          weekOffset={weekOffsets[0]}
          teams={teams}
          sort={sort}
          eventsAwayHome={eventsAwayHome}
          settings={settings}
        />
      </TabPanel>
      <TabPanel value={currentTab} index={1} className="tab-panel-current">
        <WeekTabContent
          foundWeek={foundWeeks[1]}
          requestCount={requestCount[1]}
          maxRequests={MAX_REQUESTS}
          getWeekEventsData={
            getWeekEvents.data as WeekEventsQueryData | undefined
          }
          clubId={clubId}
          weekOffset={weekOffsets[1]}
          teams={teams}
          sort={sort}
          eventsAwayHome={eventsAwayHome}
          settings={settings}
        />
      </TabPanel>
      <TabPanel value={currentTab} index={2} className="tab-panel-next">
        <WeekTabContent
          foundWeek={foundWeeks[2]}
          requestCount={requestCount[2]}
          maxRequests={MAX_REQUESTS}
          getWeekEventsData={
            getWeekEvents.data as WeekEventsQueryData | undefined
          }
          clubId={clubId}
          weekOffset={weekOffsets[2]}
          teams={teams}
          sort={sort}
          eventsAwayHome={eventsAwayHome}
          settings={settings}
        />
      </TabPanel>
    </Box>
  );
};
